import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../App';

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await api.get(`/org/invite/${token}/validate`);
        setInvite(res.data);
        setEmail(res.data.email || '');
        // Pre-fill name for existing users
        if (res.data.existing_user_name) {
          setName(res.data.existing_user_name);
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired invite link');
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    try {
      const res = await api.post(`/org/invite/${token}/accept`, { name, email, password });
      localStorage.setItem('token', res.data.token);
      setUser(res.data.user);
      navigate('/');
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to accept invite');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-zinc-400">Validating invite...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="text-4xl font-black tracking-tight mb-6">
            <span className="text-white">nex</span>
            <span className="text-nextide-turquoise">tide</span>
          </div>
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8">
            <div className="text-red-400 text-lg font-semibold mb-2">Invite Invalid</div>
            <p className="text-zinc-400 text-sm mb-6">{error}</p>
            <button onClick={() => navigate('/login')} className="text-nextide-turquoise hover:underline text-sm">
              Go to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user already belongs to this org
  if (invite.already_in_org) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="text-4xl font-black tracking-tight mb-6">
            <span className="text-white">nex</span>
            <span className="text-nextide-turquoise">tide</span>
          </div>
          <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8">
            <div className="text-nextide-turquoise text-lg font-semibold mb-2">Already a Member</div>
            <p className="text-zinc-400 text-sm mb-6">
              You're already a member of <span className="text-white font-medium">{invite.org_name}</span>. Just log in to get started.
            </p>
            <button 
              onClick={() => navigate('/login')} 
              className="w-full bg-nextide-turquoise text-black font-bold py-3 rounded-lg hover:opacity-90 transition-opacity"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isExistingUser = invite.existing_user;

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="text-4xl font-black tracking-tight mb-2">
            <span className="text-white">nex</span>
            <span className="text-nextide-turquoise">tide</span>
          </div>
          <p className="text-zinc-500">Automata</p>
        </div>

        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-nextide-turquoise/10 border border-nextide-turquoise/20 text-nextide-turquoise px-4 py-2 rounded-lg text-sm mb-4">
              {isExistingUser ? 'Link your account to' : "You've been invited to join"}
            </div>
            <h2 className="text-2xl font-bold text-white">{invite.org_name}</h2>
            <p className="text-zinc-500 text-sm mt-1">as {invite.role === 'admin' ? 'an admin' : 'a team member'}</p>
          </div>

          {/* Existing user info banner */}
          {isExistingUser && (
            <div className="bg-blue-500/10 border border-blue-500/20 text-blue-400 px-4 py-3 rounded-lg mb-6 text-sm">
              Welcome back, <span className="font-medium text-blue-300">{invite.existing_user_name}</span>! 
              Enter your existing password to link your account to this organization.
            </div>
          )}

          {submitError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Only show name field for new users */}
            {!isExistingUser && (
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
                  placeholder="Full name"
                  required
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
                readOnly
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">
                {isExistingUser ? 'Your Existing Password' : 'Create Password'}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-nextide-turquoise"
                placeholder={isExistingUser ? 'Enter your current password' : 'Min 8 characters'}
                minLength={isExistingUser ? 1 : 8}
                required
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-nextide-turquoise text-black font-bold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {submitting 
                ? (isExistingUser ? 'Linking account...' : 'Creating account...') 
                : (isExistingUser ? 'Link Account & Join' : 'Join Organization')
              }
            </button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => navigate('/login')} className="text-zinc-400 hover:text-white text-sm">
              {isExistingUser ? 'Go to login instead' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteAccept;
